<script setup lang="ts">
  import { Locale, type TranslatableLabel, type VueComponentPropsBase } from '@/types';
  import IconButton from '@/components/common/formElements/buttons/IconButton.vue';
  import { useI18n } from 'vue-i18n';
  import { onMounted, ref, computed, watch, useTemplateRef, nextTick, defineAsyncComponent } from 'vue';
  import vMarkdown, { type MarkdownClasses } from '@/directives/v-markdown';
  import ElButton from '@/components/common/formElements/buttons/ElButton.vue';
  import type {
    MarkdownBinding,
    MarkdownBindingAttrs,
  } from '@/directives/v-markdown';
  import SvgIcon from '@/components/common/SvgIcon/SvgIcon.vue';
  import useLocale from '@/composables/useLocale';
  import useGetContextualHelp from '@/api/sirta/useGetContextualHelp.api';
  import { CONTEXTUAL_HELP_TOGGLE } from '@/cypaths/cyComponents.ts';

  type ContextualHelpClassNames = 'button' | 'modal' | 'expandableContainer';

  defineOptions({ name: 'ContextualHelp', inheritAttrs: false });

  const {
    helpId,
    displayType = 'expandable',
    helpTitle,
    ...props
  } = defineProps<Partial<VueComponentPropsBase<ContextualHelpClassNames>> & {
    /** ID de l'aide contextuel à charger */
    helpId: string;
    /** L'aide peut s'afficher dans une modale ou sous forme expandable (expand/collapse) */
    displayType?: 'modal' | 'expandable';
    /** Titre qui sera affiché dans la modale */
    helpTitle?: string;
    /**
     * Binding optionnel à passer en attribut à la directive v-markdown pour spécifier les classes à ajouter sur
     * les éléments HTML ainsi que la liste des tags supportée par l'instance, s'il y a lieu.
     * */
    markdownBinding?: MarkdownBindingAttrs;
  }>();

  const h3IconCustomStyle = [
    '[background:left_2px/1em_url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIj4KICA8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTQuNjUwMzcgMC44MTM0NzlDNC4xMjAwNCAwLjU1ODUzNSAzLjU3NDIzIDAuMjk2MTUgMy4xMDk4OSAwLjE1MTIwNkMyLjYyNTYxIDQuNjMyNDdlLTA1IDEuNzE5ODUgLTAuMTg5NDUgMC44OTE3OTUgMC40MjM5NDNDMC4wNjM3NDY4IDEuMDM3MzMgLTAuMDA4MjM2OTggMS45NTExMSAwLjAwMTU3OTAyIDIuNDUzNzRDMC4wMTA5ODcgMi45MzU2OCAwLjExMTIxMSAzLjUyODE0IDAuMjA4NTk1IDQuMTAzOEwwLjg2MzQxMSA3Ljk4MjY0TDAuMjA2NDk5IDExLjg5NTZDMC4xMDk0OTkgMTIuNDcyMiAwLjAwOTcwNjk3IDEzLjA2NTQgMC4wMDA3MDY5NzggMTMuNTQ3OEMtMC4wMDg2NjEwMiAxNC4wNTA2IDAuMDY0MDU5IDE0Ljk2NTQgMC44OTM4ODMgMTUuNTc4MUMxLjcyMzcxIDE2LjE5MDggMi42MzAwOSAxNS45OTkgMy4xMTQxNCAxNS44NDY3QzMuNTc4NTUgMTUuNzAwNiA0LjEyNDM4IDE1LjQzNjggNC42NTQ5MyAxNS4xODAzTDEyLjM0NTkgMTEuNDY1N0MxMy4yMTQzIDExLjA0NjQgMTQuMDE5IDEwLjY1NzkgMTQuNTkzMiAxMC4yNjk4QzE1LjE3NDYgOS44NzY4OCAxNi4wMDEzIDkuMTY3MzYgMTYgNy45ODE5MkMxNS45OTg3IDYuNzk2NDggMTUuMTcwNCA2LjA4ODczIDE0LjU4ODIgNS42OTdDMTQuMDEzMSA1LjMxMDE1IDEzLjIwNzYgNC45MjM0MiAxMi4zMzgyIDQuNTA2MDNMNC42NTAzNyAwLjgxMzQ3OVpNMi4zNDEyMiA3LjIxNjk2SDguMDE4MTZDOC40NTU4NCA3LjIxNjk2IDguODEwNjQgNy41NjgyNCA4LjgxMDY0IDguMDAxNkM4LjgxMDY0IDguNDM0OTYgOC40NTU4NCA4Ljc4NjI0IDguMDE4MTYgOC43ODYyNEgyLjMzNTM0TDEuNzg1NTEgMTIuMDYxNEMxLjU2NDczIDEzLjM3NjYgMS40NTQzNCAxNC4wMzQyIDEuODQxNTMgMTQuMzIwMUMyLjIyODcyIDE0LjYwNiAyLjgzMzg2IDE0LjMxMzggNC4wNDQxMyAxMy43MjkyTDExLjU1MSAxMC4xMDM0QzEzLjQ2MDggOS4xODExMiAxNC40MTU4IDguNzE5OTIgMTQuNDE0OSA3Ljk4MzZDMTQuNDE0MSA3LjI0NzI4IDEzLjQ1ODIgNi43ODgxNiAxMS41NDY0IDUuODY5OTRMNC4wNDI3MSAyLjI2NTg3QzIuODMyODEgMS42ODQ3NSAyLjIyNzg3IDEuMzk0MTkgMS44NDEzOSAxLjY4MDQ3QzEuNDU0OTMgMS45NjY3NSAxLjU2NTc3IDIuNjIzMzQgMS43ODc0NCAzLjkzNjVMMi4zNDEyMiA3LjIxNjk2WiIgZmlsbD0iY3VycmVudENvbG9yIi8+Cjwvc3ZnPg==)_no-repeat]',
    'pl-[1.5em]',
  ].join(' ');

  const ElModal = defineAsyncComponent(() => import('@/components/common/ElModal/ElModal.vue'));

  const locale = useLocale();
  const { t } = useI18n();
  const { fetchHelp, isFetching } = useGetContextualHelp();
  const markdownData = ref<TranslatableLabel>({
    [Locale.fr]: '',
    [Locale.en]: '',
  });
  const expanded = defineModel<boolean>({ default: false });
  const helpContainer = useTemplateRef<HTMLDivElement>('helpContainer');

  const isExpandable = computed<boolean>(() => displayType === 'expandable');

  const ariaControls = computed<string | undefined>(() => isExpandable.value ? helpId : undefined);

  const title = computed<string>(() => helpTitle ?? t('commonComponents.contextualHelp.title'));

  const markdownAttrTranslated = computed<MarkdownBinding>(() => {
    const classes: MarkdownClasses = props.markdownBinding?.classes ?? { h3: '' };
    const h3Icon = displayType === 'modal' ? h3IconCustomStyle : '';
    classes.h3 = [classes.h3 ?? '', h3Icon].join(' ');

    return props.markdownBinding
      ? { text: markdownData.value[locale.value], ...props.markdownBinding, classes }
      : { text: markdownData.value[locale.value], classes };
  });

  const hasMarkdownData = computed<boolean>(() => !!markdownData.value[locale.value]);

  const loadContent = async (): Promise<void> => {
    const { data } = await fetchHelp(helpId);

    if (data) {
      markdownData.value = data.content;
    }
  };

  const handleHelpButtonClick = () => {
    expanded.value = !expanded.value;
    if (isExpandable.value && expanded.value) {
      nextTick(() => {
        if (helpContainer.value) {
          helpContainer.value.focus();
        }
      });
    }
  };

  watch(() => helpId, loadContent);
  onMounted(loadContent);

</script>
<template>
  <div
    v-if="!isFetching && hasMarkdownData"
    class="print:hidden"
    :class="classes?.wrapper ?? $attrs.class ?? 'contents'"
  >
    <slot
      name="button"
      :click-handler="handleHelpButtonClick"
    >
      <IconButton
        class="text-blue-accessible [grid-area:helpButton] relative -right-1"
        :class="[classes?.button, {'contextual-help--closed': !expanded}]"
        icon="question-mark-round"
        :label="t('commonComponents.contextualHelp.helpButton.showHelp')"
        :title="title"
        :data-cy="CONTEXTUAL_HELP_TOGGLE"
        size="default"
        :aria-controls="ariaControls"
        :aria-expanded="isExpandable ? expanded : undefined"
        :data-gtm-help-id="helpId"
        @click="handleHelpButtonClick"
      />
    </slot>
    <template v-if="!isExpandable">
      <ElModal
        :data-cy="helpId"
        :open="expanded"
        :class="['context-help-modal', classes?.modal]"
        @close="expanded=false"
      >
        <template #header>
          <div class="h3 font-bold flex flex-row gap-2 text-left">
            <SvgIcon
              icon="question-mark-round-filled"
              class="flex-none self-start pt-1"
            />
            {{ title }}
          </div>
        </template>
        <div class="grid grid-flow-row grid-rows-[1fr_min-content] h-full gap-8 px-5 pt-8">
          <div
            v-markdown="markdownAttrTranslated"
            class="text-left max-wrap"
          />
          <div class="pb-8">
            <ElButton
              primary
              class="md:justify-self-center"
              @click="expanded = false"
            >
              {{ t('generic.close.label') }}
            </ElButton>
          </div>
        </div>
      </ElModal>
    </template>
    <template v-else>
      <div
        v-show="expanded"
        :id="ariaControls"
        ref="helpContainer"
        data-testid="expanded-content"
        class="context-help-expandable flex flex-col mt-6 rounded border-2 border-blue-accessible bg-blue-extra-light p-2 md:container [grid-area:help]"
        :class="classes?.expandableContainer"
        tabindex="-1"
      >
        <ElButton
          link
          class="self-end no-underline! min-w-10 md:min-w-0"
          :aria-label="expanded ? t('commonComponents.contextualHelp.helpButton.hideHelp') : t('commonComponents.contextualHelp.helpButton.showHelp')"
          @click="expanded=false"
        >
          <SvgIcon icon="close-round" />
        </ElButton>
        <slot>
          <div
            v-markdown="markdownAttrTranslated"
            class="p-3 max-wrap"
          />
        </slot>
      </div>
    </template>
  </div>
</template>
